import { useEffect, useState } from "react";
import navigatorPermissionList from "../../config/defaults/navigatorPermissionList";
import navigatorPermissionStatus from "../../config/defaults/navigatorPermissionStatus";
import videoCameraTypes from "../../config/defaults/videoCameraTypes";

function usePermissions(options) {
  const showInitialPrompt =
    typeof options?.showInitialPrompt === "boolean"
      ? options.showInitialPrompt
      : true;

  const requiredPermissions = [
    navigatorPermissionList.CAMERA,
    navigatorPermissionList.MICROPHONE,
  ];

  const requiredPermissionsConfig = {
    audio: true,
    video: {
      facingMode: videoCameraTypes.FRONT,
    },
  };

  const [hasAudioPermission, setHasAudioPermission] = useState(false);
  const [isAudioPermissionBlocked, setIsAudioPermissionBlocked] =
    useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [isCameraPermissionBlocked, setIsCameraPermissionBlocked] =
    useState(false);

  const getPermissionState = async (permission) => {
    const permissionInfo = await navigator.permissions
      .query({ name: permission })
      .catch(() => null);

    return permissionInfo?.state || null;
  };

  const promptPermissionGrant = () => {
    navigator.mediaDevices
      .getUserMedia(requiredPermissionsConfig)
      .catch((e) => {
        const DomExceptionError = {
          DENIED: "permission denied",
          DISMISSED: "permission dismissed",
        };
        if (e?.message?.toLowerCase() === DomExceptionError.DENIED) {
          !isAudioPermissionBlocked && setIsAudioPermissionBlocked(true);
          !isCameraPermissionBlocked && setIsCameraPermissionBlocked(true);
        }
      });
  };

  const handlePermissionGrantChange = (
    permission,
    currentState,
    showPrompt
  ) => {
    switch (permission) {
      case navigatorPermissionList.CAMERA: {
        if (currentState === navigatorPermissionStatus.GRANTED) {
          !hasCameraPermission && setHasCameraPermission(true);
          isCameraPermissionBlocked && setIsCameraPermissionBlocked(false);
        } else if (currentState === navigatorPermissionStatus.DENIED) {
          hasCameraPermission && setHasCameraPermission(false);
          !isCameraPermissionBlocked && setIsCameraPermissionBlocked(true);
        } else if (currentState === navigatorPermissionStatus.PROMPT) {
          hasCameraPermission && setHasCameraPermission(false);
          isCameraPermissionBlocked && setIsCameraPermissionBlocked(false);
          showPrompt && promptPermissionGrant();
        }
        break;
      }
      case navigatorPermissionList.MICROPHONE: {
        if (currentState === navigatorPermissionStatus.GRANTED) {
          !hasAudioPermission && setHasAudioPermission(true);
          isAudioPermissionBlocked && setIsAudioPermissionBlocked(false);
        } else if (currentState === navigatorPermissionStatus.DENIED) {
          hasAudioPermission && setHasAudioPermission(false);
          !isAudioPermissionBlocked && setIsAudioPermissionBlocked(true);
        } else if (currentState === navigatorPermissionStatus.PROMPT) {
          hasAudioPermission && setHasAudioPermission(false);
          isAudioPermissionBlocked && setIsAudioPermissionBlocked(false);
          showPrompt && promptPermissionGrant();
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  useEffect(() => {
    if (requiredPermissions?.length > 0) {
      requiredPermissions.map((x) => {
        navigator.permissions.query({ name: x }).then(function (permission) {
          // get permission status initially
          handlePermissionGrantChange(x, permission.state, showInitialPrompt);

          // listen to permission changes
          permission.onchange = function () {
            handlePermissionGrantChange(x, permission.state, showInitialPrompt);
          };
        });
        return null;
      });

      // prompt for permission grant initially
      // eslint-disable-next-line react-hooks/exhaustive-deps
      showInitialPrompt && promptPermissionGrant();
    }
  }, []);

  return {
    requiredPermissions,
    requiredPermissionsConfig,
    hasAudioPermission,
    hasCameraPermission,
    isAudioPermissionBlocked,
    isCameraPermissionBlocked,
    getPermissionState,
    promptPermissionGrant,
  };
}

export default usePermissions;
